import { avisarPontoBloqueado } from './ponto-bloqueio';
import { emEmergencia, estaSemConexao, servidorRespondeu, suspeitarQueda, ehFalhaDeRede, guardarUsuario } from './emergencia';

const TOKEN_KEY = 'fiodecobre.token';
const MOTIVO_LOGIN_KEY = 'fiodecobre.motivo_login';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/**
 * Por que a pessoa foi parar no /login. Sobrevive ao `window.location.href` (o React remonta do
 * zero, entao estado em memoria evapora) e some quando a aba fecha. Hoje so o corte diario das
 * 05h escreve aqui (DA-414) — sem isso a equipe amanhecia num login mudo, sem saber se tinha
 * sido derrubada, se a senha mudou ou se o sistema caiu.
 */
export function lerMotivoLogin(): string | null {
  const m = sessionStorage.getItem(MOTIVO_LOGIN_KEY);
  if (m) sessionStorage.removeItem(MOTIVO_LOGIN_KEY);
  return m;
}

/**
 * Paginas que existem SEM credencial de equipe. Nelas, um 401 do ERP significa "nao sou da
 * loja agora" — nunca "va fazer login".
 *
 * ⚠️ Sem esta lista o 401 arrastava o AppCel pra dentro do sistema, e nao era hipotese: o
 * `AuthProvider` dispara `/api/auth/me` em toda abertura de pagina sempre que existe token do
 * ERP guardado, e o corte diario das 05h (DA-414) mata esse token todo dia. Resultado medido em
 * 16/08/2026 — o Ruan abriu o AppCel no iPhone, caiu na tela de login do ERP e, depois de
 * digitar a senha, no Dashboard (Caixa, Orcamentos). Fechar e abrir "resolvia" so porque o
 * `setToken(null)` abaixo ja tinha apagado o token na ida. Repetia toda manha, pra qualquer
 * pessoa da equipe com o AppCel no celular.
 *
 * ⛔ `/balcao` fica de FORA de proposito: aquela tela roda com a conta BALCAO e precisa de
 * sessao de equipe. A DA-414 ja a isenta do corte justamente pra ela nao amanhecer num
 * formulario de login virado pro cliente.
 *
 * ⚠️ Rota publica nova entra AQUI. O que decide nao e' o endereco, e' a pergunta "esta pagina
 * funciona sem alguem da loja logado?" — se sim, ela nao pode ser levada pro /login.
 */
const PAGINAS_SEM_EQUIPE = ['/app', '/catalogo', '/p', '/privacidade', '/trocas-e-devolucoes', '/termos', '/a-loja'];

function paginaAtualDispensaEquipe(): boolean {
  const p = window.location.pathname;
  return PAGINAS_SEM_EQUIPE.some((base) => p === base || p.startsWith(`${base}/`));
}

export class ApiError extends Error {
  status: number;
  detalhes?: unknown;
  /** Corpo JSON completo da resposta de erro — pra ler campos extras (ex: cliente_existente num 409). */
  corpo?: any;
  constructor(status: number, message: string, detalhes?: unknown, corpo?: unknown) {
    super(message);
    this.status = status;
    this.detalhes = detalhes;
    this.corpo = corpo;
  }
}

export interface ApiOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
}

export const MSG_EMERGENCIA = 'Indisponivel no modo emergencia — o sistema esta fora do ar.';

/**
 * Respostas do MODO EMERGENCIA (lib/emergencia.ts): com o servidor fora, a chamada nem sai daqui.
 * Quem sabe responder offline (produtos, clientes, orcamento da emergencia) se registra; devolve
 * `undefined` quando nao e' com ele. Sem ninguem que responda, a chamada falha com MSG_EMERGENCIA
 * na hora — sem esperar o tempo-limite do navegador.
 */
type RespostaEmergencia = (method: string, url: URL, opts: ApiOptions) => Promise<unknown> | undefined;
const respostasEmergencia: RespostaEmergencia[] = [];
export function registrarRespostaEmergencia(f: RespostaEmergencia): void {
  respostasEmergencia.push(f);
}
/** Ve cada resposta de GET que deu certo — a copia do modo emergencia guarda as que ela usa. */
type ObservadorResposta = (url: URL, dados: unknown) => void;
const observadores: ObservadorResposta[] = [];
export function registrarObservadorResposta(f: ObservadorResposta): void {
  observadores.push(f);
}

async function request<T>(method: string, path: string, opts: ApiOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }

  if (emEmergencia()) {
    for (const f of respostasEmergencia) {
      const r = f(method, url, opts);
      if (r !== undefined) return (await r) as T;
    }
    throw new ApiError(503, MSG_EMERGENCIA);
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(url.pathname + url.search, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    // Aborto pedido pela propria tela (busca nova cancelando a anterior) nao e' queda.
    if (ehFalhaDeRede(e) && !opts.signal?.aborted) suspeitarQueda();
    throw e;
  }
  // 502/503/504 = o nginx respondeu mas a API nao (processo caido ou reiniciando).
  if (res.status === 502 || res.status === 503 || res.status === 504) suspeitarQueda();
  // Qualquer outra resposta prova que o servidor esta vivo: apaga a tarja "Sem conexao" na hora,
  // sem esperar a proxima sondagem.
  else if (res.status < 500 && estaSemConexao()) servidorRespondeu();

  const contentType = res.headers.get('content-type') ?? '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (res.status === 401) {
    setToken(null);
    guardarUsuario(null);
    if (!path.includes('/auth/login') && !paginaAtualDispensaEquipe()) {
      // Guarda o motivo ANTES de sair — a mensagem do backend e a unica explicacao que a tela de
      // login vai ter (ver lerMotivoLogin). Sem isso o 401 do corte diario e indistinguivel de
      // token corrompido ou senha revogada.
      const msg = (data as any)?.mensagem;
      if ((data as any)?.erro === 'sessao_encerrada' && msg) sessionStorage.setItem(MOTIVO_LOGIN_KEY, msg);
      window.location.href = '/login';
    }
  }
  // Trava de PONTO (31/08/2026): defesa em profundidade da navegacao. O desvio normal e' o
  // `ExigirPontoDoDia` do App.tsx, que impede o Layout de montar; isto aqui pega a chamada
  // que escapar dele — uma aba aberta desde ontem, um `setInterval` de tela antiga, o
  // instante entre bater a saida e a tela reagir.
  //
  // ⚠️ **Ate 11/09/2026 isto trocava a pagina da aba** (`window.location.href`), e era isso
  // que sequestrava as telas de quem estava trabalhando — 137 casos medidos em 9 dias
  // uteis, ver `ponto-bloqueio.ts`. Agora so AVISA: a aba fica onde esta e o
  // `AvisoPontoFechado` desenha o bloqueio por cima.
  //
  // ⚠️ **A guarda do endereco atual nao e' zelo, e' o que evita aviso em cima do REMEDIO**:
  // a propria tela de bater ponto fala com `/api/ponto/*`, e se uma dessas respostas
  // trouxesse 403 (banco fora, por exemplo) o bloqueio apareceria justamente por cima da
  // tela que existe pra resolve-lo.
  if (res.status === 403 && (data as any)?.erro === 'ponto_pendente') {
    if (window.location.pathname !== '/ponto/entrada-do-dia' && !paginaAtualDispensaEquipe()) {
      avisarPontoBloqueado();
    }
  }
  if (!res.ok) {
    // Aceita 'erro' (pt) e 'error' (en) — alguns endpoints antigos usam 'error'.
    const erroBase = (data as any)?.erro ?? (data as any)?.error ?? `${res.status}`;
    const detalhes = (data as any)?.detalhes;
    const detalhe = detalhesZodParaTexto(detalhes);
    const erro = detalhe ? `${erroBase} — ${detalhe}` : erroBase;
    throw new ApiError(res.status, erro, detalhes, data);
  }
  if (method === 'GET') {
    for (const f of observadores) {
      try { f(url, data); } catch { /* copia da emergencia nunca derruba a tela */ }
    }
  }
  return data as T;
}

// Traducao curta de erros Zod comuns pra portugues.
const TRADUCAO_ZOD: Record<string, string> = {
  'Invalid email': 'email invalido',
  'Required': 'obrigatorio',
  'Expected string, received null': 'obrigatorio',
};

const NOME_CAMPO: Record<string, string> = {
  nome: 'nome',
  login: 'login',
  senha: 'senha',
  email: 'email',
  cpf_cnpj: 'CPF/CNPJ',
  telefone: 'telefone',
  whatsapp: 'WhatsApp',
  perfil: 'perfil',
  razao_social: 'razao social',
  nome_fantasia: 'nome fantasia',
  sigla: 'sigla',
};

function detalhesZodParaTexto(detalhes: unknown): string | null {
  if (!detalhes || typeof detalhes !== 'object') return null;
  const fieldErrors = (detalhes as { fieldErrors?: Record<string, string[]> }).fieldErrors;
  if (!fieldErrors) return null;
  const partes: string[] = [];
  for (const [campo, msgs] of Object.entries(fieldErrors)) {
    if (!msgs || msgs.length === 0) continue;
    const m = msgs[0]!;
    let msg = TRADUCAO_ZOD[m] ?? m.toLowerCase();
    if (/string must contain at least (\d+)/i.test(m)) {
      const min = m.match(/at least (\d+)/i)?.[1];
      msg = `minimo ${min} caracteres`;
    }
    if (/string must contain at most (\d+)/i.test(m)) {
      const max = m.match(/at most (\d+)/i)?.[1];
      msg = `maximo ${max} caracteres`;
    }
    partes.push(`${NOME_CAMPO[campo] ?? campo}: ${msg}`);
  }
  return partes.length ? partes.join('; ') : null;
}

// POST que devolve ARQUIVO (PDF), nao JSON. O `request` acima le o corpo como json/texto e um PDF
// viraria string corrompida. Erro continua chegando como JSON — o backend so manda o binario no
// caminho feliz —, entao a mensagem do 400 segue legivel na tela.
async function postBlob(path: string, body?: unknown): Promise<Blob> {
  if (emEmergencia()) throw new ApiError(503, MSG_EMERGENCIA);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(path, {
    method: 'POST',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let erro = String(res.status);
    try {
      const j = await res.json();
      erro = (j as any)?.erro ?? (j as any)?.error ?? erro;
    } catch { /* corpo nao-JSON: fica o status */ }
    throw new ApiError(res.status, erro);
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string, opts?: ApiOptions) => request<T>('GET', path, opts),
  postBlob,
  post: <T>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('PATCH', path, { ...opts, body }),
  delete: <T>(path: string, opts?: ApiOptions) => request<T>('DELETE', path, opts),
};
